import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StaleTime } from '@common/ts/enums';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import { API_ENDPOINTS } from '@/api-endpoints';

// ── Types ─────────────────────────────────────────────────────────────

export interface ResearchThread {
  _id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
  message_count: number;
}

export interface ResearchMessage {
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  content: any;
  created_at: string;
}

export interface ResearchArtifact {
  artifact_id: string;
  type: 'quote_collection' | 'data_table' | 'theme_summary' | 'bar_chart' | 'markdown_report';
  title: string;
  data: any;
  created_at: string;
}

export interface ResearchThreadFull {
  _id: string;
  title: string;
  messages: ResearchMessage[];
  artifacts: ResearchArtifact[];
  working_memory: {
    active_filter_id: string | null;
    last_discussed_question_id: string | null;
    cited_respondents: number[];
    turn_count: number;
  };
  shared: boolean;
  total_input_tokens: number;
  total_output_tokens: number;
  createdAt: string;
  updatedAt: string;
}

// ── Query keys ────────────────────────────────────────────────────────
// We accept undefined here so the queryKey is stable even before the
// projectId resolves from the route. If projectId is undefined, the key
// becomes ['research-threads', '_no_project_', 'all'] — which is distinct
// from any real project key, preventing accidental query sharing.

const NO_PROJECT = '_no_project_';

const KEYS = {
  threads: (projectId: string | undefined, filterId?: string | null) =>
    ['research-threads', projectId ?? NO_PROJECT, filterId ?? 'all'] as const,
  thread: (projectId: string | undefined, threadId: string | undefined) =>
    ['research-thread', projectId ?? NO_PROJECT, threadId ?? '_no_thread_'] as const,
  analysisSchemaStatus: (projectId: string | undefined, filterId?: string | null) =>
    ['ra-analysis-schema-status', projectId ?? NO_PROJECT, filterId ?? 'all'] as const,
};

// ── Types ─────────────────────────────────────────────────────────────

/**
 * Shape of the /analysis-schema-status endpoint response.
 * - `current: true`  → project has the rich schema the RA/Compare need
 * - `current: false, reason: 'no_analysis'` → project has never been analysed
 * - `current: false, reason: 'old_schema'`  → project was analysed before the
 *   rework and needs to be re-analysed to unlock RA/Compare
 */
export interface AnalysisSchemaStatus {
  current: boolean;
  reason?: 'no_analysis' | 'old_schema';
  /** ISO timestamp of when the analysis was last completed. Used to
   *  mark RA threads created before this date as "stale". */
  analyzed_at?: string;
}

// ── Hooks ─────────────────────────────────────────────────────────────

export const useListThreads = (projectId?: string, filterId?: string | null) =>
  useQuery<ResearchThread[]>({
    queryKey: KEYS.threads(projectId, filterId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.LIST_THREADS(projectId!, filterId),
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetThread = (projectId?: string, threadId?: string | null) =>
  useQuery<ResearchThreadFull>({
    queryKey: KEYS.thread(projectId, threadId ?? undefined),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.GET_THREAD(projectId!, threadId!),
      );
      return data;
    },
    enabled: !!projectId && !!threadId,
    staleTime: StaleTime.LONG,
    retry: 0, // don't retry NotFound — the thread was just deleted
  });

export const useCreateThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filterId: string | null) => {
      const { data } = await axiosInstanceAnalyze.post(
        API_ENDPOINTS.RESEARCH_ASSISTANT.CREATE_THREAD(projectId!),
        { filter_id: filterId },
      );
      return data as { _id: string; title: string };
    },
    onSuccess: () => {
      // Invalidate ALL filter buckets for this project so the new thread
      // shows up regardless of which filter the user is viewing. Using
      // just the project key matches every (filterId) variant.
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
    },
  });
};

export const useDeleteThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => {
      await axiosInstanceAnalyze.delete(
        API_ENDPOINTS.RESEARCH_ASSISTANT.DELETE_THREAD(projectId!, threadId),
      );
    },
    onSuccess: () => {
      // Filter scope unknown at delete time — invalidate all thread lists
      // for THIS project (not all projects).
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
    },
  });
};

export interface SuggestedQuestion {
  category: 'explore' | 'quotes' | 'compare' | 'report';
  text: string;
}

export const useGetSuggestions = (projectId?: string, filterId?: string | null) =>
  useQuery<SuggestedQuestion[]>({
    queryKey: ['research-suggestions', projectId, filterId ?? 'all'] as const,
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.SUGGESTIONS(projectId!, filterId),
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useUpdateThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      updates,
    }: {
      threadId: string;
      updates: { title?: string; shared?: boolean };
    }) => {
      const { data } = await axiosInstanceAnalyze.patch(
        API_ENDPOINTS.RESEARCH_ASSISTANT.UPDATE_THREAD(projectId!, threadId),
        updates,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
    },
  });
};

/**
 * Pre-flight check that tells the frontend whether the project's
 * overall_pages analysis has the rich schema the Research Assistant
 * and Compare features depend on.
 *
 * Returns { current: true } when the project is ready, or
 * { current: false, reason } when the frontend should prompt the
 * user to re-run analysis via the OutdatedAnalysisDialog.
 *
 * Cached aggressively — the status is stable between user actions
 * (only changes when analysis is (re-)run) so we cache for the whole
 * staleTime window and refetch on window focus to catch the case
 * where the user triggered a re-run in another tab.
 */
export const useAnalysisSchemaStatus = (
  projectId?: string,
  filterId?: string | null,
) =>
  useQuery<AnalysisSchemaStatus>({
    queryKey: KEYS.analysisSchemaStatus(projectId, filterId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.ANALYSIS_SCHEMA_STATUS(projectId!, filterId),
      );
      return data as AnalysisSchemaStatus;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
    refetchOnWindowFocus: true,
    retry: 0,
  });

/**
 * Helper for components that want to invalidate the schema status
 * cache — used after a force re-run completes so the dialog sees
 * the new "current: true" state on its next read.
 */
export const useInvalidateAnalysisSchemaStatus = () => {
  const queryClient = useQueryClient();
  return (projectId?: string) => {
    // Invalidate all filter buckets for this project — we don't know
    // which filter the re-run touched so we refetch all of them.
    queryClient.invalidateQueries({
      queryKey: ['ra-analysis-schema-status', projectId ?? NO_PROJECT],
    });
  };
};
